/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Archive, Calendar, Kanban, List, Star } from 'lucide-react';

/**
 * The view switch's icons.
 *
 * These are not decoration: below the `laptop` breakpoint every button except the
 * current one drops its label, and the icon is all that is left. Without them the
 * switch renders as four blank buttons — which is exactly what it did the first
 * time this component was written.
 *
 * **The glyphs are lucide's**, at one weight with the rest of the app. They were
 * hand-drawn copies of the vanilla app's, and they said the same five things:
 * three columns, three lines, a month, a star, a box. Same meanings, drawn by the
 * same hand as every other icon on the bar.
 *
 * **Two of them were then chosen again, on the paths rather than on the names.**
 * These render at 16px and lucide draws on a 24px grid, so the question is not
 * which icon is the best picture of a board — it is which one survives being
 * shrunk by a third. Read out of the installed package:
 *
 *   - `Columns3` is an 18×18 rounded rectangle with two dividers inside it. At
 *     this size the container is most of the ink and the three columns are two
 *     faint lines in a box — it read as "a box", which is what the switch's most
 *     important tab was wearing. `Kanban` is three bare vertical strokes at
 *     three heights and nothing else: no container to dominate, and the
 *     different lengths carry the idea of columns holding different amounts.
 *   - `CalendarDays` is a calendar with **six dots** on it, each drawn as a
 *     zero-length `h.01` stroke relying on the round cap. Six round caps inside
 *     a 16px box is a smudge. `Calendar` is the same frame without them, and
 *     nothing about a month is lost by dropping the days.
 *
 * `List` and `Archive` were left alone: both are three or four strokes already
 * and neither has anything to lose. `List` also happens to be the exact
 * counterpart of `Kanban` — horizontal against vertical — so the two tabs that
 * show the same work two ways say so in the glyphs.
 */
// 16px, up from 14. Lucide's 2px stroke scales with the box, so a smaller icon
// is also a fainter one; at 14 these were noticeably lighter than the 14px
// semibold label beside them and the pair read as mismatched weights.
const common = { className: 'size-4 shrink-0', 'aria-hidden': true } as const;

export const VIEW_ICONS = {
  board: <Kanban {...common} />,
  list: <List {...common} />,
  calendar: <Calendar {...common} />,
  todo: <Star {...common} />,
  archived: <Archive {...common} />,
} as const;
